/**
 * Contenido del manual de Config Cápsula (UI).
 * Fuente extendida: el manual del producto, en /manual (ver `?p=40-mapa`, el mapa de
 * configuracion). **Decia `documentacion/operacion/MANUAL_CONFIG_CAPSULA.md`, que no existe**
 * —corregido el 2026-10-01—: un puntero a un archivo inexistente manda a buscar media hora
 * una fuente que nadie escribio, que es peor que no tener puntero.
 */

export const CAPSULE_MANUAL_SECTIONS = [
  {
    id: 'intro',
    title: '0. Introducción',
    blocks: [
      { type: 'p', text: 'Una cápsula (ej. sistemas) agrupa agentes, guías de negocio, vocabulario de clasificación y política de habilidades. Cada conversación y ticket pertenece a una cápsula.' },
      { type: 'h', text: 'División de responsabilidades' },
      { type: 'ul', items: [
        'Guías cápsula → criterios inyectados en prompts LLM (routing, tickets, SLA…)',
        'Labels / Intents / Prioridades → vocabulario del JSON de clasificación',
        'Agentes → coordinator: SOUL/RULES + habilidades; agentes de línea: cómo responden',
        'Habilidades (cápsula) → qué habilidades pueden asignarse a agentes de esta cápsula',
        'Coordinación → gate KB y rondas del motor post-turno react_v1',
      ]},
      { type: 'h', text: 'Flujo de un mensaje' },
      { type: 'ol', items: [
        'Clasificación previa (JSON: suggested_agent, create_ticket, intent, priority…)',
        'Agente de línea responde al usuario',
        'Si hay ticket: coordinador post-turno (blackboard, subtareas, delegación)',
      ]},
      { type: 'note', text: 'No existen matrices routing_rules en la API. La política es Markdown + SOUL/RULES + listas opcionales.' },
    ],
  },
  {
    id: 'coordinacion',
    title: '1. Coordinación',
    blocks: [
      { type: 'p', text: 'Pestaña Coordinacion: parámetros técnicos del runtime, no criterios de negocio narrativos.' },
      { type: 'h', text: 'coordinator_engine' },
      { type: 'p', text: 'Valor fijo react_v1. Tras cada respuesta del agente de línea, si hay ticket_id, el coordinador puede usar herramientas de plataforma (blackboard, subtareas, delegar_agente, HITL).' },
      { type: 'h', text: 'kb_retrieval_gate' },
      { type: 'p', text: 'Solo si el agente de línea tiene habilidad knowledge-search.' },
      { type: 'ul', items: [
        'off (default): siempre ejecuta búsqueda vectorial (RAG) cuando corresponde',
        'llm: un micro-clasificador decide si consultar KB; usa la guía "Uso de KB (gate llm)". Fail-open si falla el LLM.',
      ]},
      { type: 'h', text: 'max_coordinator_tool_rounds' },
      { type: 'p', text: 'Rondas LLM + herramientas del coordinador post-turno por pasada (máx. 10). Más rondas = más subtareas encadenadas, más latencia. Afecta solo al coordinador, no al agente de línea.' },
      { type: 'h', text: 'max_tool_rounds' },
      { type: 'p', text: 'Rondas de tools HTTP y workspace del agente conversacional (default 5, tope 20). Si en Turn Health / Logs aparece max_tool_rounds_reached o agent_runtime.max_tool_rounds_reached, subí este valor.' },
      { type: 'h', text: 'wall_clock_timeout_sec_per_llm_call' },
      { type: 'p', text: 'Timeout por llamada LLM del agente (15–600s, default 120). No es el techo HTTP de turno; la suma de llamadas debe caber bajo http_turn_budget_sec.' },
      { type: 'h', text: 'max_messages_context' },
      { type: 'p', text: 'Cuántos mensajes recientes se inyectan al prompt (4–100, default 24). Más contexto = más tokens/latencia.' },
      { type: 'h', text: 'context_note_omitted_messages' },
      { type: 'p', text: 'Si true (default) y hay más mensajes que la ventana, antepone una nota + el primer mensaje de usuario para no perder el pedido original.' },
      { type: 'h', text: 'allowed_tool_risk_tiers' },
      { type: 'p', text: 'Tiers permitidos: read, write, external. El tier destructive está disponible en UI con warning fuerte y default OFF: solo habilitalo si entendés el riesgo (p.ej. DELETE remoto).' },
      { type: 'h', text: 'http_turn_budget_sec (plataforma)' },
      { type: 'p', text: 'Techo HTTP sync de plataforma (env HTTP_TURN_BUDGET_SEC, default 600). No se edita por cápsula. El Asistente de ticket, axios y el nginx host deben respetarlo. Los presupuestos internos (wall_clock LLM, max_tool_rounds, rondas KB) deben caber bajo este techo; si no, verás 504 / http_gateway_timeout sin Turn Health del runtime.' },
      { type: 'note', text: 'Runbook ops: OmniSyS/docs/nginx-http-turn-budget.md. Autopsia del chat de ticket: conversation_id ticket-chat-{ticket_id}.' },
    ],
  },
  {
    id: 'guias',
    title: '2. Guías cápsula',
    blocks: [
      { type: 'p', text: 'Texto Markdown guardado en Mongo e inyectado en prompts. No ejecuta código: el LLM interpreta tus criterios.' },
      { type: 'h', text: 'JSON de clasificación (cada mensaje nuevo)' },
      { type: 'code', text: `{
  "intent": "...",
  "priority": "medium",
  "category": "...",
  "suggested_agent": "n1_apps",
  "label": "incidente",
  "confidence": 0.85,
  "reasoning": "breve",
  "create_ticket": true
}` },
      { type: 'p', text: 'suggested_agent debe ser un agent_id activo en Agentes → esta cápsula.' },
      { type: 'h', text: 'ticket_create_policy.materialize_on' },
      { type: 'ul', items: [
        'classification (default compat): si create_ticket=true en el JSON, la plataforma inserta el ticket antes del agente (email/WA/Teams/webchat).',
        'agent_tool_only: create_ticket es solo señal; no inserta. El ticket nace con omnisys_ticket_create cuando las RULES del agente de línea lo autorizan tras relevamiento.',
      ]},
      { type: 'p', text: 'Recomendado para mesa de ayuda (Sistemas): agent_tool_only + guía Tickets + RULES N1. Evita tickets fantasma por un mail vago.' },
      { type: 'note', text: 'Usá el botón "¿Qué poner aquí?" en cada bloque de la pestaña Guías para detalle por campo.' },
    ],
  },
  {
    id: 'skills',
    title: '3. Habilidades (cápsula)',
    blocks: [
      { type: 'p', text: 'skill_policy define qué habilidades del catálogo tenant pueden asignarse a agentes de ESTA cápsula.' },
      { type: 'ul', items: [
        'Habilitar aquí NO asigna la habilidad al agente: también hay que asignarla en Agentes → [agente] → Habilidades',
        'Habilidades nuevas del tenant llegan desactivadas hasta que las habilites',
        'Habitual en Sistemas: knowledge-search, omnisys-platform-api, secure-agent-ops',
      ]},
    ],
  },
  {
    id: 'labels',
    title: '4. Labels',
    blocks: [
      { type: 'p', text: 'Etiquetas visuales en conversaciones y tickets. Campos: id (en JSON label), name, color, description.' },
      { type: 'ul', items: [
        'No cambian el SOUL del agente; organizan la UI',
        'Se asignan por clasificación automática o corrección manual en Monitor/Tickets',
        'Lista vacía → label libre en JSON (vocabulario acotado por guías)',
      ]},
    ],
  },
  {
    id: 'sla',
    title: '4bis. SLA y horario laboral',
    blocks: [
      { type: 'p', text: 'El SLA se compromete por TIPO DE PEDIDO, no por urgencia. Cada intent tiene su primera respuesta y su resolución objetivo, y se editan en la pestaña Intents, debajo de cada uno.' },
      { type: 'note', text: 'Antes vivía en la prioridad y daba resultados absurdos: un proyecto de software marcado "high" —porque lo pidió gerencia— vencía a las 8 horas y salía en rojo desde el primer día. La prioridad dice cuánto urge; el SLA dice qué se prometió. Son ejes distintos.' },
      { type: 'h', text: 'Resolución vacía = sin reloj de cierre' },
      { type: 'p', text: 'Dejar vacía la resolución NO es cero horas: es que ese tipo de pedido no se mide con un reloj de ticket. Es lo que corresponde a lo que entra al ciclo de desarrollo, que se cierra por estimación o por hitos. La primera respuesta, en cambio, siempre tiene reloj: el ack se debe siempre.' },
      { type: 'h', text: 'El reloj corre solo en horario laboral' },
      { type: 'p', text: 'Los días que el área no trabaja no suman horas. Con jornada de 8 a 17, un pedido que entra el viernes a las 16 llega al lunes a las 9 con 2 horas de reloj, no con 65. Se configura arriba de Intents: días, ventana horaria, zona y feriados.' },
      { type: 'ul', items: [
        'Los feriados van en AAAA-MM-DD y no suman aunque caigan en día laborable.',
        'Lo que entra fuera de la ventana arranca a la apertura del siguiente día hábil.',
        'Una guardia 24x7 se configura con los siete días y de 00:00 a 23:59: no hay caso especial en el código.',
      ]},
      { type: 'h', text: 'La prioridad conserva un SLA de respaldo' },
      { type: 'p', text: 'Se usa solo en tickets sin intent: los viejos y los que entran por un canal sin clasificar. Por eso en Prioridades el campo figura como "SLA h (respaldo)". Si lo ajustás esperando cambiar el vencimiento de un tipo de pedido, no va a pasar nada.' },
      { type: 'h', text: 'Dónde se ve el resultado' },
      { type: 'p', text: 'En Ejecutivo: los KPI de vencidos y el semáforo de SLA salen de /analytics/pmo/aging, que compara la edad hábil del ticket contra el SLA de su intent.' },
    ],
  },
  {
    id: 'intents',
    title: '5. Intents, categorías y prioridades',
    blocks: [
      { type: 'p', text: 'Vocabulario que el coordinador usa al clasificar cada mensaje.' },
      { type: 'ul', items: [
        'Intents: intención (incidente, consulta, solicitud…)',
        'Categorías: área funcional (aplicaciones, infraestructura…)',
        'Prioridades: id + nombre + color. Es la urgencia del caso; el compromiso de tiempo vive en el intent.',
      ]},
      { type: 'note', text: 'Listas vacías = vocabulario libre en el JSON (más flexible, menos predecible). Recomendado definir al menos prioridades e intents clave.' },
    ],
  },
  {
    id: 'llm',
    title: '6. LLM Tenant',
    blocks: [
      { type: 'p', text: 'Proveedores LLM del tenant completo (OpenAI, Anthropic, Google, Groq, Grok). NO es por cápsula.' },
      { type: 'p', text: 'Cada agente referencia provider_ref definido aquí (Agentes → Modelo → primary/fallback).' },
    ],
  },
  {
    id: 'plantilla',
    title: '7. Plantilla Sistemas',
    blocks: [
      { type: 'p', text: 'Ejemplos copiables. Ajustá agent_id a los de tu cápsula.' },
      { type: 'h', text: 'routing_guidance' },
      { type: 'code', text: `## Lineas N1
- **n1_apps**: ERP, CRM, planificadores, errores de aplicacion.
- **n1_infra**: red, impresoras, PCs, hardware.

## Ejemplos
- "Planificador no carga" → n1_apps
- "Impresora no enciende" → n1_infra` },
      { type: 'h', text: 'ticket_guidance' },
      { type: 'code', text: `## create_ticket = true
- Incidente con impacto operativo, usuario confirma registro.

## create_ticket = false
- Saludos, consultas resueltas en el mismo turno.` },
      { type: 'h', text: 'ticket_coordination_guidance' },
      { type: 'code', text: `## Delegacion
- Diagnostico con APIs → delegar_agente a n2_apps_tools / n2_infra_tools
- Verificacion fisica → subtarea human` },
    ],
  },
  {
    id: 'faq',
    title: '8. FAQ',
    blocks: [
      { type: 'h', text: '¿Por qué no hay reglas de routing?' },
      { type: 'p', text: 'Reemplazadas por guías Markdown + SOUL/RULES. routing_rules legacy fue eliminado.' },
      { type: 'h', text: '¿Ticket en WhatsApp?' },
      { type: 'p', text: 'Habilidad omnisys-platform-api + RULES con omnisys_ticket_create y tool_calls.' },
      { type: 'h', text: '¿Guía vs SOUL?' },
      { type: 'p', text: 'Guía = criterios de negocio por cápsula. SOUL/RULES = tono, prohibiciones, formato, tools del agente.' },
      { type: 'h', text: '¿Cómo probar?' },
      { type: 'p', text: 'Agentes → Test Coordinador; Logs → Autopsia → pestañas Timeline, Informe Config y Mapa cobertura. El informe muestra qué guías cápsula (routing, tickets, SLA, KB gate, etc.) se inyectaron en cada etapa del turno.' },
    ],
  },
];

export const GUIDANCE_FIELD_HELP = {
  modelo_de_trabajo: {
    title: 'Modelo de trabajo del area',
    body: [
      { type: 'p', text: 'Como trabaja el equipo, escrito en etapas. Se inyecta ANTES del SOUL de cada agente, asi que alcanza a los cuatro: es el texto de mayor alcance de la capsula.' },
      { type: 'p', text: 'Sirve para que cada agente sepa donde encaja su turno dentro del caso, en vez de optimizar su propio mensaje. Cuando se cargo, el tiempo de varios casos bajo a la mitad porque los agentes dejaron de investigar en la etapa de entender.' },
      { type: 'p', text: 'La regla que mas rindio: se le pregunta al usuario lo que solo el usuario sabe, y se busca en el sistema lo que solo el sistema sabe.' },
      { type: 'p', text: 'No pongas aca lo especifico de un agente: eso va en su SOUL o sus RULES.' },
    ],
  },
  routing_guidance: {
    title: 'Routing',
    when: 'Cada mensaje nuevo (clasificación) y como referencia en post-turno.',
    injects: 'Sección CRITERIOS DE ENRUTAMIENTO en el prompt del orchestrator.',
    controls: 'Campo suggested_agent del JSON de clasificación.',
    template: `## Criterios
- [agent_id]: cuando usar (ejemplos concretos)
## Ambiguedad
- Que hacer si no queda claro el area
## Ejemplos
- "..." → agent_id`,
    example: 'Planificador de compras no carga → n1_apps; impresora no enciende → n1_infra',
    tip: 'Usá agent_id exactos de Agentes → esta cápsula. El botón IA genera borrador: revisá IDs antes de guardar.',
  },
  ticket_guidance: {
    title: 'Tickets (clasificación)',
    when: 'Clasificación previa de cada mensaje.',
    injects: 'Sección CRITERIOS DE TICKETS en orchestrator.',
    controls: 'Campo create_ticket: true/false en el JSON.',
    template: `## create_ticket = true
- Cuando registrar caso (incidente, impacto, confirmacion usuario)
## create_ticket = false
- Saludos, consultas sin trazabilidad`,
    example: 'Saludo "hola" → false. "No carga el modulo para todo el equipo" → true.',
    tip: 'En WhatsApp la apertura con número real suele ser omnisys_ticket_create en RULES del agente, no solo este flag.',
  },
  ticket_coordination_guidance: {
    title: 'Coordinación en ticket (post-turno)',
    when: 'Tras respuesta del agente si la conversación tiene ticket_id.',
    injects: 'Motor react_v1 del coordinator_engine.',
    controls: 'Blackboard, crear_subtarea, delegar_agente, solicitar_intervencion_humana.',
    template: `## Blackboard
- Que registrar en summary/facts
## Delegacion
- Cuando delegar_agente vs subtarea humana
## HITL
- Cuando pausar y pedir operador`,
    example: 'Error ERP complejo → delegar_agente a n2_apps_tools con payload JSON.',
    tip: 'No pegues JSON de tool_calls aquí; el kernel ya documenta las herramientas.',
  },
  sla_followup_guidance: {
    title: 'SLA y seguimiento',
    when: 'Post-turno react_v1 (mismo motor que coordinación en ticket).',
    injects: 'Sección SEGUIMIENTO SLA en coordinator_engine.',
    controls: 'Plazos, prórrogas, insistencia; convenciones en títulos de subtareas y blackboard.',
    template: `## Plazos
- Formato fechas ISO en titulos de subtarea
## Prorroga
- Cuando crear subtarea de extension
## Insistencia
- Cuando nudge al operador humano`,
    example: 'Subtarea "Prorroga hasta 2026-07-01 — espera proveedor".',
    tip: 'No inventes campos nuevos en Mongo; usá convenciones en texto de tareas/blackboard.',
  },
  vocabulary_guidance: {
    title: 'Vocabulario global',
    when: 'Solo clasificación previa (no post-turno).',
    injects: 'CRITERIOS DE VOCABULARIO en orchestrator.',
    controls: 'intent, category, label, priority en el JSON.',
    template: `## Intent
- incidente vs consulta vs solicitud
## Priority
- Que severidad operativa implica high/critical
## Label
- Cuando usar null`,
    example: 'Consulta "¿cómo cambio mi clave?" → intent consulta, priority low.',
    tip: 'Complementa las descripciones por ítem en pestaña Intents.',
  },
  kb_usage_guidance: {
    title: 'Uso de KB (gate llm)',
    when: 'Solo si Coordinacion → kb_retrieval_gate = llm.',
    injects: 'Clasificador en kb_retrieval_gate.py (antes de RAG).',
    controls: 'Si el agente de línea ejecuta búsqueda vectorial.',
    template: `## Consultar KB
- Procedimientos documentados, errores conocidos
## No consultar KB
- Saludos, charla trivial, datos no documentados`,
    example: '"Hola" → no KB. "Error ORA-00001 en módulo stock" → sí KB.',
    tip: 'Con gate off esta guía no se usa; RAG siempre corre si hay habilidad knowledge-search.',
  },
};

export const GUIDANCE_TAB_INTRO = {
  title: 'Cómo funcionan las guías',
  paragraphs: [
    'Cada bloque es Markdown guardado en Mongo e inyectado en un prompt LLM en un momento distinto del flujo.',
    'No hay reglas imperativas en base de datos: el modelo interpreta tu texto junto con SOUL/RULES del agente coordinator.',
    'Orden recomendado: (1) Intents/Prioridades, (2) Routing, (3) Tickets, (4) Coordinación post-turno + SLA, (5) Escalamiento y KB si aplica.',
  ],
};

export const COORDINACION_INLINE_EXTRA = {
  title: 'Parámetros agent_runtime',
  rows: [
    { param: 'kb_retrieval_gate = off', effect: 'RAG siempre (si hay habilidad knowledge-search)' },
    { param: 'kb_retrieval_gate = llm', effect: 'Micro-clasificador + guía Uso de KB antes de RAG' },
    { param: 'max_tool_rounds', effect: 'Rondas tools HTTP+workspace del agente (1–20). Síntoma: max_tool_rounds_reached' },
    { param: 'max_coordinator_tool_rounds', effect: 'Rondas de tools del coordinador post-turno (1–10)' },
    { param: 'wall_clock_timeout_sec_per_llm_call', effect: 'Timeout por llamada LLM (15–600s)' },
    { param: 'max_messages_context', effect: 'Ventana de mensajes al prompt (4–100)' },
    { param: 'context_note_omitted_messages', effect: 'Nota + primer user msg si se truncó el historial' },
    { param: 'allowed_tool_risk_tiers', effect: 'read/write/external; destructive default OFF' },
    {
      param: 'http_turn_budget_sec (plataforma)',
      effect:
        'Techo HTTP sync (default 600s). Rondas × timeout LLM deben caber bajo este techo. Nginx host /api y el cliente del ticket chat se alinean a este valor. No es presupuesto mensual de tokens.',
    },
  ],
};

export const SKILLS_TAB_INTRO = [
  'skill_policy filtra el catálogo tenant: solo habilidades enabled=true pueden asignarse a agentes de esta cápsula.',
  'Asignar una habilidad a un agente concreto: Agentes → [agente] → Que puede hacer.',
  'Investigación KB iterativa y workspace de ticket: Agentes → [agente] → pestaña Runtime.',
  'Habilidades nuevas del tenant aparecen desactivadas hasta que las habilites aquí.',
];

export const LLM_TAB_INTRO = [
  'Configuración a nivel tenant: todos los agentes de todas las cápsulas usan estos provider_ref.',
  'Cada agente elige primary/fallback en Agentes → Modelo.',
  'Sin API key válida el agente fallará al responder; usá Test en el proveedor.',
];
